import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search, X } from 'lucide-react';

/**
 * Multi-select dropdown — searchable, portal-rendered, chip-based.
 *
 * Chosen over a <select multiple> because:
 *   - Native multi-select UX is poor on every OS.
 *   - We need searchable filtering for long lists (locations, terminals).
 *   - Portal rendering escapes the filter-bar's overflow.
 *
 * Values flow as arrays — parent clears with [] or sets a specific list.
 */

export interface MultiOption {
  value: string;
  label: string;
  subtitle?: string;
  /**
   * Optional heading this option sits under. Options carrying a group are
   * rendered in labelled sections, in first-seen order, so a list drawn from
   * several sources (e.g. documents by folder — agreements, policies) reads as
   * separate lists rather than one run-on list. Options with no group render
   * exactly as before, ungrouped and first.
   */
  group?: string;
}

interface Props {
  options: MultiOption[];
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  /** Text shown in the trigger when N items are selected (default: "{N} selected") */
  summarize?: (selected: MultiOption[]) => string;
}

export const MultiSelectDropdown: React.FC<Props> = ({
  options, value, onChange, placeholder = 'Select...',
  searchPlaceholder = 'Search...', disabled, summarize,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const anchor = useRef<HTMLButtonElement>(null);
  const popover = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o =>
      o.label.toLowerCase().includes(q) ||
      o.value.toLowerCase().includes(q) ||
      (o.subtitle || '').toLowerCase().includes(q)
    );
  }, [options, query]);

  /**
   * `[groupLabel, rows]` in first-seen order, so the caller controls section
   * order by the order it passes options. Ungrouped options collect under ''
   * and render without a heading.
   */
  const grouped = useMemo(() => {
    const map = new Map<string, MultiOption[]>();
    for (const o of filtered) {
      const key = o.group || '';
      const rows = map.get(key);
      if (rows) rows.push(o);
      else map.set(key, [o]);
    }
    return Array.from(map.entries());
  }, [filtered]);

  const selectedSet = useMemo(() => new Set(value), [value]);
  const selected = useMemo(() => options.filter(o => selectedSet.has(o.value)), [options, selectedSet]);

  useEffect(() => {
    if (!open || !anchor.current) return;
    const reposition = () => {
      const rect = anchor.current!.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchor.current?.contains(t) || popover.current?.contains(t)) return;
      setOpen(false);
      setQuery('');
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const toggle = (v: string) => {
    if (selectedSet.has(v)) onChange(value.filter(x => x !== v));
    else onChange([...value, v]);
  };

  const label = (() => {
    if (selected.length === 0) return placeholder;
    if (summarize) return summarize(selected);
    if (selected.length === 1) return selected[0].label;
    return `${selected.length} selected`;
  })();

  return (
    <>
      <button
        ref={anchor}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        className="w-full h-10 px-3 flex items-center gap-2 rounded-lg border border-stone-300 bg-white text-sm hover:border-stone-400 disabled:opacity-50"
      >
        <span className={`flex-1 text-left truncate ${selected.length > 0 ? 'text-stone-800 font-medium' : 'text-stone-400'}`}>
          {label}
        </span>
        {selected.length > 0 && (
          <span
            onClick={(e) => { e.stopPropagation(); onChange([]); }}
            className="text-stone-400 hover:text-stone-600 shrink-0"
            title="Clear"
          >
            <X size={13} />
          </span>
        )}
        <ChevronDown size={14} className={`text-stone-500 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && pos && createPortal(
        <div
          ref={popover}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: Math.max(pos.width, 260), zIndex: 10000 }}
          className="bg-white rounded-xl shadow-2xl border border-stone-200 py-2 max-h-80 overflow-hidden flex flex-col"
        >
          <div className="px-2 pb-2 border-b border-stone-100">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-8 pr-2 h-8 rounded-md border border-stone-200 text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-emerald-500"
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-center text-sm text-stone-400">No matches</div>
            )}
            {grouped.map(([group, rows]) => (
              <div key={group || '__ungrouped__'}>
                {group && (
                  <p className="px-2.5 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-stone-400">
                    {group}
                  </p>
                )}
                {rows.map(o => {
              const checked = selectedSet.has(o.value);
              return (
                <button
                  key={o.value}
                  onClick={() => toggle(o.value)}
                  className="w-full px-2.5 py-1.5 flex items-center gap-2 hover:bg-stone-50 text-left"
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                    checked ? 'bg-emerald-600 border-emerald-600' : 'border-stone-300 bg-white'
                  }`}>
                    {checked && <Check size={11} className="text-white" strokeWidth={3} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-stone-800 truncate">{o.label}</p>
                    {o.subtitle && <p className="text-[11px] text-stone-500 truncate font-mono">{o.subtitle}</p>}
                  </div>
                </button>
              );
                })}
              </div>
            ))}
          </div>
          {value.length > 0 && (
            <div className="border-t border-stone-100 px-2 pt-2 flex items-center justify-between">
              <span className="text-[11px] text-stone-500">{value.length} selected</span>
              <button
                onClick={() => onChange([])}
                className="text-[11px] text-stone-500 hover:text-stone-700 inline-flex items-center gap-1"
              >
                <X size={11} /> Clear
              </button>
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
};

export default MultiSelectDropdown;
